import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getTypeColor } from "./utils";

export default function StatisticsTab({ stats, loading }) {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!stats) {
    return <Alert severity="info">No statistics available</Alert>;
  }

  return (
    <>
      {/* Overall Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography
                color="text.secondary"
                gutterBottom
                variant="body2"
                p={0}
                m={0}
              >
                Total Messages
              </Typography>
              <Typography variant="h4" fontWeight="bold" p={0} m={0}>
                {stats.overall?.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography
                color="text.secondary"
                gutterBottom
                variant="body2"
                p={0}
                m={0}
              >
                Read Messages
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="success.main"
                p={0}
                m={0}
              >
                {stats.overall?.read || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography
                color="text.secondary"
                gutterBottom
                variant="body2"
                p={0}
                m={0}
              >
                Unread Messages
              </Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="warning.main"
                p={0}
                m={0}
              >
                {stats.overall?.unread || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography
                color="text.secondary"
                gutterBottom
                variant="body2"
                p={0}
                m={0}
              >
                Read Rate
              </Typography>
              <Typography variant="h4" fontWeight="bold" p={0} m={0}>
                {stats.overall?.readRate ? `${stats.overall.readRate}%` : "0%"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Breakdown Table */}
      {stats.breakdown && stats.breakdown.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
            Messages Breakdown by Type & Category
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: theme.palette.background.paper }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">Read</TableCell>
                  <TableCell align="center">Unread</TableCell>
                  <TableCell align="center">Read Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.breakdown.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip
                        label={item.type}
                        color={getTypeColor(item.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.category || "general"}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} p={0} m={0}>
                        {item.total}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color="success.main"
                        p={0}
                        m={0}
                      >
                        {item.read}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color="warning.main"
                        p={0}
                        m={0}
                      >
                        {item.unread}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" p={0} m={0}>
                        {item.readRate ? `${item.readRate}%` : "0%"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );
}
